import React from 'react';
import { connect } from 'react-redux';
import Avatar from '@material-ui/core/Avatar';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import withStyles from '@material-ui/core/styles/withStyles';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import DoneIcon from '@material-ui/icons/Done';
import WarningIcon from '@material-ui/icons/WarningRounded';

import { toLongTimeString } from 'utilities/formatters/dateTimeFormatter';
import { Body1, Body2, Label } from 'components/Typography';
import VisitorPassEngine from 'areas/visitorpass/domain/visitorPassEngine';
import CardHolderView from 'areas/cards/components/CardHolderView';
import VisitorPassView from 'areas/visitorpass/components/VisitorPassView';
import isEmptyObject from 'utilities/is/isEmptyObject';
import isStringSet from 'utilities/is/isStringSet';
import { Mobile, Tablet, Desktop } from 'components/Responsive';
import repository from 'client/repository';
import { processCard } from 'areas/cards/domain';
import alertActions from 'areas/alert/actions';
import swipeEvents from '../../domain/swipeEvents';
import styles from './styles';
import displayMode from 'areas/swipes/domain/displayMode';

class SwipeListItem extends React.Component {
    state = {
        metadataVisible: false,
        activeSwipe: null,
        activeVisitorPass: null,
        activeCard: null
    };

    visitorEngine = new VisitorPassEngine();

    toggleMetadata = (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.setState({ metadataVisible: !this.state.metadataVisible });

        return false;
    };

    /** Handles the callback from the swipe list, used for rendering the given swipe */
    onSwipeInfoClicked = async (swipe) => {
        this.setState({
            activeSwipe: swipe,
            activeCard: null,
            activeVisitorPass: null
        });

        try {
            if (swipe.card) {

                this.props.showProgress('Loading card details..');

                const card = await repository.cards.getCardDataByQR(swipe.card.qrCode,true,true,this.props.location.site.siteCode);
                card.cardData.dateRetrieved = new Date().toISOString();

                const processedCard = processCard(card, this.props.location, this.props.supervisorData, this.props.config);

                if (swipe.jobRoleID && processedCard.jobRolesArray.length > 0) {
                    processedCard.jobRole = processedCard.jobRolesArray.find(j => j.id === swipe.jobRoleID);
                }

                if (swipe.employerID && processedCard.employersArray.length > 0) {
                    processedCard.employer = processedCard.employersArray.find(e => e.id === swipe.employerID);
                }

                this.props.hideProgress();
                this.setState({ activeCard: processedCard });

            } else if (swipe.visitorPass) {

                this.props.showProgress('Loading visitor pass..');

                const pass = await repository.cards.getVisitorPassByQR(swipe.visitorPass.qrCode);

                this.props.hideProgress();
                this.setState({ activeVisitorPass: pass });
            }

        } catch (err) {
            this.props.hideProgress();
            this.props.alert(err.toString());
            this.setState({ activeCard: null });
        }
    };

    /** Closes the swipe details view */
    closeSwipeView = () => this.setState({ activeSwipe: null });

    getCurrentDisplayMode(activeSwipe) {
        return activeSwipe.displayMode || displayMode.historic;
    }

    cardHolderSwipeView = (activeSwipe, config, location) => {
        const hasCard = Boolean(this.state.activeCard);
        const denyReason = isStringSet(activeSwipe.denyReasonDescription)
            ? { reasonDescription: activeSwipe.denyReasonDescription, reasonID: activeSwipe.denyReasonID }
            : null;

        const showEmployerAndJobRole = hasCard && isStringSet(activeSwipe.employerID);
        const mode = this.getCurrentDisplayMode(activeSwipe);
        return (
            <React.Fragment>
                {hasCard &&
                    <CardHolderView
                        card={this.state.activeCard}
                        config={config}
                        location={location}
                        denyReason={denyReason}
                        close={this.closeSwipeView}
                        title={`${activeSwipe.card.firstname} ${activeSwipe.card.surname}`}
                        open={true}
                        editable={false}
                        viewcontext={mode}
                        cardHolderProps={{
                            selectedEmployerID: hasCard && (this.state.activeCard.selectedEmployerID || ''),
                            selectedJobRoleID: hasCard && (this.state.activeCard.selectedJobRoleID || ''),
                            employerAndJobRoleEditable: false,
                            showEmployerAndJobRole: showEmployerAndJobRole,
                            fatigueDataEditable: false
                        }} />
                }
            </React.Fragment>
        );
    };

    visitorPassSwipeView = (activeSwipe, config, location) => {
        const pass = this.state.activeVisitorPass;
        const hasPass = Boolean(pass);

        let vpStatus = { status: '', isWarning: false };

        if (hasPass) {
            vpStatus = this.visitorEngine.getVisitorPassStatus(this.visitorEngine.validateVisitorPass(pass, location));
        }

        return (
            <React.Fragment>
                {hasPass &&
                    <VisitorPassView
                        pass={pass}
                        status={vpStatus}
                        config={config}
                        close={this.closeSwipeView}
                        title={`${activeSwipe.visitorPass.firstname} ${activeSwipe.visitorPass.surname}`}
                        open={true}
                        editable={false} />
                }
            </React.Fragment>
        );
    };

    swipeView = () => {
        const { config, location } = this.props;
        const { activeSwipe } = this.state;
        const open = !!activeSwipe;
        const isCardView = open && !isEmptyObject(activeSwipe.card);
        const isVisitorPassView = open && !isEmptyObject(activeSwipe.visitorPass);

        return (
            <React.Fragment>
                {isCardView &&
                    this.cardHolderSwipeView(activeSwipe, config, location)}
                {isVisitorPassView &&
                    this.visitorPassSwipeView(activeSwipe, config, location)}
            </React.Fragment>);
    };

    swipeTypeText = (swipe) => {
        const isError = swipeEvents.isError(swipe.swipeEvent);

        return (
            <Grid container spacing={1} alignItems="flex-start" alignContent="flex-start">
                <Grid item>
                    <Body2 className={isError ? this.props.classes.warning : this.props.classes.ok}>
                        {isError ? <WarningIcon /> : <DoneIcon />}
                    </Body2>
                </Grid>
                <Grid item>
                    <Body2 className={isError ? this.props.classes.warning : ''}>
                        {swipe.swipeEvent} {swipe.competenceName ? ' | ' + swipe.competenceName : ''}
                    </Body2>
                </Grid>
            </Grid>);
    };

    jobRole = (swipe, swipeData) => {

        if (!isStringSet(swipe.jobRoleID)) {
            return '';
        }

        if (isStringSet(swipeData.serialNumber)) {
            if (swipeData.jobRolesArray.length > 0) {
                const role = swipeData.jobRolesArray.find(j => j.id.toLowerCase() === swipe.jobRoleID.toLowerCase());
                if (role) {
                    return `${role.name} (${role.division})`;
                }
            }
        }

        return '';
    };

    employer = (swipe, swipeData) => {
        if (!isStringSet(swipe.employerID)) {
            return '';
        }

        if (isStringSet(swipeData.serialNumber)) {
            if (swipeData.employersArray.length > 0) {
                const employer = swipeData.employersArray.find(e => e.id.toLowerCase() === swipe.employerID.toLowerCase());
                if (employer) {
                    return employer.name;
                }
            }
        }

        return '';
    };

    render() {
        const { classes, swipe } = this.props;
        const { metadataVisible } = this.state;
        const swipeData = swipe.card || swipe.visitorPass;

        if (!swipeData) {
            return <React.Fragment></React.Fragment>
        }

        const jobRole = this.jobRole(swipe, swipeData);
        const employer = this.employer(swipe, swipeData);

        return (
            <React.Fragment>
                {this.state.activeSwipe &&
                    this.swipeView()
                }

                <ListItem className={classes.card} button onClick={() => this.onSwipeInfoClicked(swipe)}>
                    <ListItemAvatar className={classes.avatar}>
                        <Avatar className={classes.avatarImage} src={"data:image/png;base64," + swipeData.photo} alt="Card holder photo" />
                    </ListItemAvatar>
                    <Mobile>
                        <Grid container className={classes.content}>
                            {!metadataVisible
                                ? <Grid item xs={12}>
                                    <Label className={classes.primaryText} strong>
                                        {swipeData.firstname + ' ' + swipeData.surname}
                                    </Label>
                                    <Body2>
                                        {swipeData.skillSafeNo || swipeData.visitorPassNumber}
                                    </Body2>
                                    <Body2>
                                        {toLongTimeString(swipe.swipeTime)}
                                    </Body2>
                                    {this.swipeTypeText(swipe)}
                                </Grid>
                                : <Grid item xs={12}>
                                    {swipeData.employer &&
                                        <Body1 className={classes.primaryText}>
                                            {employer}
                                        </Body1>
                                    }
                                    {jobRole &&
                                        <Body2>
                                            {jobRole}
                                        </Body2>
                                    }
                                    <Body2>
                                        {swipe.projectName}
                                    </Body2>
                                    <Body2>
                                        {swipe.siteName}
                                    </Body2>
                                    {swipe.zoneName &&
                                        <Body2>
                                            {swipe.zoneName}
                                        </Body2>
                                    }
                                </Grid>
                            }
                        </Grid>
                        <ListItemSecondaryAction>
                            <IconButton aria-label="Information" onClick={this.toggleMetadata}>
                                <InfoIcon />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </Mobile>
                    <Tablet>
                        <Grid container className={classes.content}>
                            <Grid item sm={6}>
                                <Label className={classes.primaryText} strong>
                                    {swipeData.firstname + ' ' + swipeData.surname}
                                </Label>
                                <Body2 className={classes.primaryText}>
                                    {swipeData.skillSafeNo || swipeData.visitorPassNumber} | {toLongTimeString(swipe.swipeTime)}
                                </Body2>
                                {this.swipeTypeText(swipe)}
                            </Grid>
                            <Grid item sm={6}>
                                {swipe.card &&
                                    <Body2 className={classes.primaryText}>
                                        {employer}
                                    </Body2>
                                }
                                {jobRole &&
                                    <Body2>
                                        {jobRole}
                                    </Body2>
                                }
                                <Body2>
                                    {swipe.projectName} | {swipe.siteName} {swipe.zoneName && ` | ${swipe.zoneName}`}
                                </Body2>
                            </Grid>
                        </Grid>
                    </Tablet>
                    <Desktop>
                        <Grid container className={classes.content}>
                            <Grid item xs={12} sm={4}>
                                <Label className={classes.primaryText} strong>
                                    {swipeData.firstname + ' ' + swipeData.surname}
                                </Label>
                                <Body2>
                                    {swipeData.skillSafeNo || swipeData.visitorPassNumber}
                                </Body2>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Body2 strong className={classes.primaryText}>
                                    {toLongTimeString(swipe.swipeTime)}
                                </Body2>
                                {this.swipeTypeText(swipe)}
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                {swipe.card &&
                                    <Body2 className={classes.primaryText} strong>
                                        {employer}
                                    </Body2>
                                }
                                {jobRole &&
                                    <Body2>
                                        {jobRole}
                                    </Body2>
                                }
                                <Body2>
                                    {swipe.projectName} | {swipe.siteName} {swipe.zoneName && ` | ${swipe.zoneName}`}
                                </Body2>
                            </Grid>
                        </Grid>
                    </Desktop>
                </ListItem>
                <Divider />
            </React.Fragment>
        );
    }
}

function mapStateToProps(state) {
    return {};
}

function mapDispatchToProps(dispatch) {
    return {
        alert: (message) => dispatch(alertActions.error(message))
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(SwipeListItem));
